import { useI18n, type MessageKey } from '@ecom/i18n';
import { Field } from '@ecom/ui-kit';
import { zodResolver } from '@hookform/resolvers/zod';
import { type ReactElement } from 'react';
import { useForm } from 'react-hook-form';

import { updateOrderStatusFormSchema, type UpdateOrderStatusFormValues } from '../schemas';
import type { MerchantDrivenStatus } from '../types';

import { whenDefined } from '@/lib/optional-props';

const TARGET_LABEL_KEY: Record<MerchantDrivenStatus, MessageKey> = {
  PROCESSING: 'merchant.orders.targetProcessing',
  PACKED: 'merchant.orders.targetPacked',
  CANCELLED: 'merchant.orders.targetCancel',
};

/**
 * `targetStatus` is fixed by the caller (`OrderTable`'s own, deliberately
 * narrow "which single transition applies right now" hint — a UX
 * affordance, never the authority: `OrderStateMachine`, server-side,
 * decides what is actually legal, and this form's own submission still
 * goes through the real `PATCH .../status` unchanged). `reason` is the
 * one field a merchant fills in — optional, exactly like the API's own
 * schema.
 */
export function UpdateStatusForm({
  targetStatus,
  onSubmit,
  isSubmitting,
}: {
  targetStatus: MerchantDrivenStatus;
  onSubmit: (values: UpdateOrderStatusFormValues) => void;
  isSubmitting: boolean;
}): ReactElement {
  const { t } = useI18n();
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<UpdateOrderStatusFormValues>({
    resolver: zodResolver(updateOrderStatusFormSchema),
    defaultValues: { status: targetStatus, reason: '' },
  });

  return (
    <form
      id="update-order-status-form"
      onSubmit={(event) => {
        void handleSubmit(onSubmit)(event);
      }}
      noValidate
      className="flex flex-col gap-4"
    >
      <fieldset disabled={isSubmitting} className="contents">
        <input type="hidden" {...register('status')} />

        <p className="text-sm text-ink">{t(TARGET_LABEL_KEY[targetStatus])}</p>

        <Field
          label={t('merchant.orders.reasonLabel')}
          optional
          {...whenDefined('error', errors.reason?.message)}
        >
          {(bind) => <textarea {...bind} {...register('reason')} rows={3} className="ecom-input" />}
        </Field>
      </fieldset>
    </form>
  );
}
